<template>
    <Timetable
        :classes="classes"
        :value="date"
        :isFetchingData="isFetchingData"
        :errorInFetching="errorInFetching"
        @input="onDateChange"
        @onEndOfTable="onEndOfPage"
    />
</template>
<script>
import Timetable from '@/components/browseclasses/timetable/Timetable.vue'
import { groupClassesByDate } from '@/util/classes/classFormat'
import { getClassesFromStudioId } from '@/util/cloudFunctions/classesFunctions'
import { getDaysAfterDateString } from '@/util/datetimehelpers'

export default {
    components: {
        Timetable,
    },
    props: {
        studio: Object,
        classFilter: Array,
        instructorFilter: Array,
        locationFilter: Array,
        date: String,
    },
    data() {
        return {
            classes: [],
            isFetchingData: true,
            errorInFetching: false,
            lastClassIds: {}, // Store lastClassId for each date
            loadedAllData: false,
            rawClasses: [],
        }
    },
    async created() {
        await this.setClasses()
        window.addEventListener('scroll', this.handleScroll)
    },
    beforeDestroy() {
        if (typeof window === 'undefined') return
        window.removeEventListener('scroll', this.handleScroll)
    },
    watch: {
        classFilter: {
            handler: async function () {
                await this.setClasses()
            },
            deep: true,
        },
        instructorFilter: {
            handler: async function () {
                await this.setClasses()
            },
            deep: true,
        },
        locationFilter: {
            handler: async function () {
                await this.setClasses()
            },
            deep: true,
        },
        date: {
            handler: async function () {
                await this.setClasses()
            },
        },
    },
    methods: {
        async setClasses() {
            this.isFetchingData = true
            this.lastClassIds = {} // Reset lastClassIds when setting new classes

            // Create array of promises for parallel execution
            const promises = Array.from({ length: 7 }, async (_, i) => {
                const currentDate = getDaysAfterDateString(
                    this.date,
                    this.studio.timezone,
                    i
                )

                const input = {
                    studioId: this.studio.id,
                    classFilter: this.classFilter,
                    instructorFilter: this.instructorFilter,
                    locationFilter: this.locationFilter,
                    startDate: currentDate,
                    endDate: currentDate,
                    limit: 25,
                }

                const result = await getClassesFromStudioId(input)
                if (!result.success) {
                    throw new Error('Failed to fetch classes')
                }

                // Store lastClassId for this date if there are classes
                if (result.docs.length > 0) {
                    this.lastClassIds[currentDate] = result.lastClassId
                }

                return result.docs
            })

            try {
                const results = await Promise.all(promises)
                const allClasses = results.flat()
                this.rawClasses = allClasses
                this.classes = groupClassesByDate(
                    allClasses,
                    this.studio.timezone
                )
                this.errorInFetching = false
            } catch (error) {
                this.errorInFetching = true
            } finally {
                this.isFetchingData = false
            }
        },
        async onEndOfPage() {
            if (this.isFetchingData || this.loadedAllData) {
                return
            }

            this.isFetchingData = true

            // Create array of promises for parallel execution
            const promises = Array.from({ length: 7 }, async (_, i) => {
                const currentDate = getDaysAfterDateString(
                    this.date,
                    this.studio.timezone,
                    i
                )

                // Skip if we don't have a lastClassId for this date
                if (!this.lastClassIds[currentDate]) {
                    return []
                }

                const input = {
                    studioId: this.studio.id,
                    classFilter: this.classFilter,
                    instructorFilter: this.instructorFilter,
                    locationFilter: this.locationFilter,
                    startDate: currentDate,
                    endDate: currentDate,
                    lastClassId: this.lastClassIds[currentDate],
                    limit: 25,
                }

                const result = await getClassesFromStudioId(input)
                if (!result.success) {
                    throw new Error('Failed to fetch classes')
                }

                if (result.docs.length > 0) {
                    this.lastClassIds[currentDate] = result.lastClassId
                    return result.docs
                } else {
                    // Remove the lastClassId for this date since there's no more data
                    delete this.lastClassIds[currentDate]
                    return []
                }
            })

            try {
                const results = await Promise.all(promises)
                const allClasses = results.flat()

                if (allClasses.length === 0) {
                    this.loadedAllData = true
                    this.errorInFetching = false
                    return
                }

                this.rawClasses = [...this.rawClasses, ...allClasses]
                this.classes = groupClassesByDate(
                    this.rawClasses,
                    this.studio.timezone
                )
                this.errorInFetching = false
            } catch (error) {
                this.errorInFetching = true
            } finally {
                this.isFetchingData = false
            }
        },
        onDateChange(date) {
            this.loadedAllData = false
            this.$emit('update:date', date)
        },
    },
}
</script>
